import { useState, type CSSProperties } from "react"
import {
  portalChecklistProgress,
  portalTableColumnKind,
  type PortalAnswerValue,
  type PortalDraftValue,
  type PortalRequestResponse,
} from "@ledgerbox/domain"

import {
  amountFieldText,
  amountMinusNote,
  amountSign,
  amountToSend,
  canAddTableRow,
  pickedOf,
  tableRowsToSend,
  tableRowsToShow,
  togglePick,
  withTableCell,
  withTableRow,
  withoutTableRow,
} from "./item-answers"
import {
  draftAnswerKey,
  draftNote,
  type DraftStatus,
  type ItemDraft,
} from "./item-draft"
import { isSentBack } from "./item-state"
import { useTableDraft, type useTextDraft } from "./use-item-draft"

/* The answer controls for the kinds that take more than a word: a choice
   with several answers, a checklist, an amount and a table. The row around
   them (name, example, thread) is ChecklistItem's. */

type Item = PortalRequestResponse["items"][number]
type Save = (value: PortalAnswerValue | null) => Promise<void>

/**
 * A checklist, or a choice that takes several answers. Each press is the
 * answer at once, so there is nothing to send and no draft to keep.
 */
export function PickAnswer({
  item,
  pending,
  onSave,
}: {
  item: Item
  pending: boolean
  onSave: Save
}) {
  const picked = pickedOf(item.answer?.value)
  const press = (option: string) =>
    void onSave(togglePick(item.options, picked, option))

  if (item.kind === "checklist") {
    return (
      <div className="todo-answer">
        <ul className="todo-list" aria-label={item.name}>
          {item.options.map((option) => (
            <li key={option}>
              <label className="todo">
                <input
                  type="checkbox"
                  checked={picked.includes(option)}
                  disabled={pending}
                  onChange={() => press(option)}
                />
                <span>{option}</span>
              </label>
            </li>
          ))}
        </ul>
        <p className="todo-count tabular">
          {portalChecklistProgress(item.options, picked)}
        </p>
      </div>
    )
  }

  // Several answers are checkboxes, as a checklist is: a tick says what is
  // chosen, where a pressed capsule said it only with a fill (R9-U1).
  const hintId = `pick-hint-${item.itemId}`
  return (
    <>
      <span className="d" id={hintId}>
        Pick all that apply
      </span>
      <div
        className="todo-answer pick-answer"
        role="group"
        aria-label={item.name}
        aria-describedby={hintId}
      >
        <ul className="todo-list">
          {item.options.map((option) => (
            <li key={option}>
              <label className="todo">
                <input
                  type="checkbox"
                  checked={picked.includes(option)}
                  disabled={pending}
                  onChange={() => press(option)}
                />
                <span>{option}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </>
  )
}

/** An amount in the firm's currency. The field keeps a draft while the
 * client types; Send takes "$1,250.50" and keeps "1250.50". */
export function AmountAnswer({
  item,
  draft,
  pending,
  onSave,
}: {
  item: Item
  draft: ReturnType<typeof useTextDraft>
  pending: boolean
  onSave: Save
}) {
  const [invalid, setInvalid] = useState(false)
  const next = amountToSend(draft.value, draft.answered)
  const problemId = `amount-problem-${item.itemId}`
  const minus = amountMinusNote(draft.value, item.answer?.currency)
  return (
    <form
      className="answer-field"
      onSubmit={(event) => {
        event.preventDefault()
        if (next.kind === "invalid") setInvalid(true)
        if (next.kind === "send") void onSave(next.value)
      }}
    >
      <span className="amount-input">
        <span className="amount-sign" aria-hidden="true">
          {amountSign(item.answer?.currency)}
        </span>
        <input
          aria-label={item.name}
          aria-invalid={invalid}
          aria-describedby={`draft-${item.itemId}${invalid ? ` ${problemId}` : ""}`}
          className="mono"
          inputMode="decimal"
          autoComplete="off"
          maxLength={24}
          placeholder="0.00"
          value={amountFieldText(draft.value, draft.answered)}
          onChange={(event) => {
            setInvalid(false)
            draft.change(event.currentTarget.value)
          }}
        />
      </span>
      <SendOrClear
        same={next.kind === "same" && Boolean(item.answer)}
        pending={pending}
        disabled={next.kind === "empty"}
        onClear={() => void onSave(null)}
        onKeep={
          isSentBack(item)
            ? () => void onSave(item.answer?.value ?? null)
            : undefined
        }
      />
      {invalid && next.kind === "invalid" ? (
        <p id={problemId} className="field-problem" role="alert">
          {next.problem}
        </p>
      ) : minus ? (
        <p className="draft-note">{minus}</p>
      ) : null}
      {/* A rejected amount is not saved as an answer, so "Saved" under it
          would say the opposite of the error above it. */}
      <DraftNote
        id={`draft-${item.itemId}`}
        status={draft.status}
        value={draft.value}
        answered={draft.answered}
        quiet={invalid}
      />
    </form>
  )
}

/**
 * A table the firm set the columns of. The client adds rows, up to 50. On a
 * phone each row stacks into labelled fields, so nothing scrolls sideways.
 */
export function TableAnswer({
  item,
  pending,
  onDraftSaved,
  onSave,
}: {
  item: Item
  pending: boolean
  onDraftSaved(draft: ItemDraft): void
  onSave: Save
}) {
  const draft = useTableDraft(item, onDraftSaved)
  const columns = item.options
  const rows = tableRowsToShow(draft.value, columns)
  const toSend = tableRowsToSend(rows)
  const same =
    Boolean(item.answer) &&
    draftAnswerKey(rows) === draftAnswerKey(draft.answered)
  const send = async (value: PortalAnswerValue | null) => {
    await draft.settle()
    await onSave(value)
  }
  return (
    <form
      className="table-answer"
      style={{ "--table-columns": columns.length } as CSSProperties}
      onSubmit={(event) => {
        event.preventDefault()
        if (toSend && !same) void send(toSend)
      }}
    >
      <div className="table-answer-head" aria-hidden="true">
        {columns.map((column) => (
          <span key={column} title={column}>
            {column}
          </span>
        ))}
      </div>
      {rows.map((cells, rowIndex) => (
        <fieldset className="table-answer-row" key={rowIndex}>
          <legend className="visually-hidden">Row {rowIndex + 1}</legend>
          {columns.map((column, columnIndex) => (
            <label key={column}>
              <span className="table-answer-label visually-hidden">
                {column}
              </span>
              {/* A date column takes a date and a money column a figure,
                  read from the column's name (R9-U5). */}
              <input
                autoComplete="off"
                maxLength={500}
                type={portalTableColumnKind(column) === "date" ? "date" : "text"}
                inputMode={
                  portalTableColumnKind(column) === "money" ? "decimal" : undefined
                }
                className={
                  portalTableColumnKind(column) === "money"
                    ? "mono table-answer-money"
                    : undefined
                }
                value={cells[columnIndex] ?? ""}
                onChange={(event) =>
                  draft.change(
                    withTableCell(
                      rows,
                      { row: rowIndex, column: columnIndex },
                      event.currentTarget.value
                    )
                  )
                }
              />
            </label>
          ))}
          {rows.length > 1 ? (
            <button
              type="button"
              className="text-button"
              aria-label={`Remove row ${rowIndex + 1}`}
              onClick={() => draft.change(withoutTableRow(rows, rowIndex))}
            >
              Remove
            </button>
          ) : null}
        </fieldset>
      ))}
      <div className="table-answer-actions">
        {canAddTableRow(rows) ? (
          <button
            type="button"
            className="text-button"
            onClick={() => draft.change(withTableRow(rows, columns))}
          >
            Add a row
          </button>
        ) : (
          <span className="d">The table is full at 50 rows</span>
        )}
        <SendOrClear
          same={same}
          pending={pending}
          disabled={!toSend}
          onClear={() => void send(null)}
          onKeep={
            isSentBack(item)
              ? () => void send(item.answer?.value ?? null)
              : undefined
          }
        />
      </div>
      <DraftNote
        id={`draft-${item.itemId}`}
        status={draft.status}
        value={rows}
        answered={draft.answered}
      />
    </form>
  )
}

/** Send while the field differs from the answer; once it is the answer, a
 * button that says it takes the answer back from the firm (R9-U4). It sits
 * in Send's own slot at Send's size, so the field has one control beside it
 * (R9-U7). On an answer the firm sent back, that control is "Keep my
 * answer": taking it back does not answer the firm. */
export function SendOrClear({
  same,
  pending,
  disabled,
  onClear,
  onKeep,
}: {
  same: boolean
  pending: boolean
  disabled: boolean
  onClear(): void
  /** Set while the firm has the answer sent back. */
  onKeep?: () => void
}) {
  if (same && onKeep)
    return (
      <button
        type="button"
        className="ghost-pill"
        disabled={pending}
        onClick={onKeep}
      >
        {pending ? "Sending…" : "Keep my answer"}
      </button>
    )
  return same ? (
    <button
      type="button"
      className="ghost-pill"
      disabled={pending}
      onClick={onClear}
    >
      Take back my answer
    </button>
  ) : (
    <button type="submit" className="ghost-pill" disabled={pending || disabled}>
      {pending ? "Sending…" : "Send"}
    </button>
  )
}

/** "Saved" under a draft, or why it was not. The id is the field's
 * description, so a screen reader hears it with the field; only a failure
 * is announced as it happens. */
export function DraftNote<V extends PortalDraftValue>({
  id,
  status,
  value,
  answered,
  quiet = false,
}: {
  id: string
  status: DraftStatus<V>
  value: V
  answered: V
  /** Holds back "Saved" while the field shows a problem. A failed save is
      still said. */
  quiet?: boolean
}) {
  const note = draftNote(status, value, answered)
  return (
    <p id={id} className={`draft-note${note?.failed ? " is-failed" : ""}`}>
      {/* Always in the page, so a failure that lands in it is read out. */}
      <span aria-live="polite">{note?.failed ? note.message : null}</span>
      {note && !note.failed && !quiet ? note.message : null}
    </p>
  )
}
